import React, { useState, useEffect, useMemo } from "react";
import { useQuery, gql, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { notify, notifyError } from "../../../utils/toast";
import clsx from "clsx";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";

const MISSION_ADMIN_CAMPAIGNS_FOR_CANCEL = gql`
  query MissionAdminCampaignsForCancel($filter: MissionAdminCampaignFilter) {
    missionAdminCampaigns(filter: $filter) {
      id
      brand {
        name
      }
      productName
      productImageUrl
      channel
      runs {
        id
        startAt
        endAt
        isActive
        participationCount
      }
    }
  }
`;

const GET_CAMPAIGN_PARTICIPATIONS = gql`
  query GetCampaignParticipations($campaignId: Int!) {
    missionAdminCampaign(campaignId: $campaignId) {
      id
      productName
      brand {
        name
      }
      runs {
        id
        startAt
        endAt
        participations {
          id
          userId
          user {
            id
            name
            phone
          }
          status
          joinedAt
          completedAt
          depositAmount
          prizeAmount
          depositChargeId
        }
      }
    }
  }
`;

const GET_PARTICIPATION = gql`
  query GetParticipation($participationId: Int!) {
    participation(participationId: $participationId) {
      id
      userId
      user {
        id
        name
        phone
      }
      status
      joinedAt
      completedAt
      depositAmount
      prizeAmount
      depositChargeId
      run {
        id
        startAt
        endAt
        campaign {
          id
          productName
          productImageUrl
          brand {
            name
          }
        }
      }
    }
  }
`;

const CANCEL_PARTICIPATION_MUTATION = gql`
  mutation MissionAdminCancelParticipation(
    $participationId: Int!
    $cancelReason: String!
  ) {
    missionAdminCancelParticipation(
      participationId: $participationId
      cancelReason: $cancelReason
    ) {
      ok
      error
    }
  }
`;

const AdminCancelParticipation = () => {
  const navigate = useNavigate();
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(
    null
  );
  const [selectedParticipationId, setSelectedParticipationId] = useState<
    number | null
  >(null);
  const [cancelReason, setCancelReason] = useState<string>("");
  const [searchId, setSearchId] = useState<number | null>(null);
  const [displayCount, setDisplayCount] = useState<number>(10);
  const [campaignDisplayCount, setCampaignDisplayCount] = useState<number>(10);

  const { data: campaignsData, loading: campaignsLoading } = useQuery(
    MISSION_ADMIN_CAMPAIGNS_FOR_CANCEL,
    {
      fetchPolicy: "network-only",
    }
  );

  const { data: participationsData, loading: participationsLoading } = useQuery(
    GET_CAMPAIGN_PARTICIPATIONS,
    {
      variables: { campaignId: selectedCampaignId },
      skip: !selectedCampaignId,
      fetchPolicy: "network-only",
    }
  );

  const { data, loading, error, refetch } = useQuery(GET_PARTICIPATION, {
    variables: { participationId: searchId },
    skip: !searchId,
    fetchPolicy: "network-only",
  });

  const [cancelParticipation] = useMutation(CANCEL_PARTICIPATION_MUTATION);

  const handleSearch = () => {
    const input = document.querySelector<HTMLInputElement>('input[type="number"]');
    const id = input ? parseInt(input.value) : null;
    if (!id || isNaN(id) || id <= 0) {
      notifyError("올바른 참여 ID를 입력해주세요.");
      return;
    }
    setSearchId(id);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleCancelParticipation = async (participationId: number) => {
    if (!cancelReason) {
      notifyError("취소 사유를 입력해주세요.");
      return;
    }

    if (cancelReason.length < 4) {
      notifyError("취소 사유는 4글자 이상 입력해주세요.");
      return;
    }

    if (
      !window.confirm("해당 참여를 취소하시겠습니까? 예치금이 환불됩니다.")
    ) {
      return;
    }

    await cancelParticipation({
      variables: {
        participationId,
        cancelReason: cancelReason.trim(),
      },
      onCompleted: (data) => {
        if (data.missionAdminCancelParticipation.ok) {
          notify("참여가 취소되었습니다. 예치금이 환불됩니다.");
          setCancelReason("");
          setSelectedParticipationId(null);
          refetch();
          if (selectedCampaignId) {
            // 캠페인별 참여 목록도 새로고침
            window.location.reload();
          }
        } else {
          notifyError(
            data.missionAdminCancelParticipation.error || "취소에 실패했습니다."
          );
        }
      },
      onError: (error) => {
        notifyError(error.message || "취소 중 오류가 발생했습니다.");
      },
    });
  };

  const participation = data?.participation;
  const isCanceled = participation?.status === "CANCELED";

  // 모든 참여 목록 수집 (최신순 정렬)
  const allParticipations = useMemo(() => {
    return (
      participationsData?.missionAdminCampaign?.runs?.flatMap(
        (run: any) => run.participations || []
      ) || []
    );
  }, [participationsData?.missionAdminCampaign?.runs]);

  // 표시할 참여 목록 (최근 10개씩)
  const displayedParticipations = useMemo(() => {
    return allParticipations.slice(0, displayCount);
  }, [allParticipations, displayCount]);

  const hasMore = useMemo(() => {
    return allParticipations.length > displayCount;
  }, [allParticipations.length, displayCount]);

  // 캠페인 변경 시 표시 개수 초기화
  useEffect(() => {
    setDisplayCount(10);
  }, [selectedCampaignId]);

  // 디버깅용 로그
  useEffect(() => {
    console.log("참여 목록 상태:", {
      전체개수: allParticipations.length,
      표시개수: displayCount,
      실제표시: displayedParticipations.length,
      더보기필요: hasMore,
    });
  }, [allParticipations.length, displayCount, displayedParticipations.length, hasMore]);

  const getChannelLabel = (channel: string) => {
    switch (channel) {
      case "NAVER":
        return "네이버";
      case "COUPANG":
        return "쿠팡";
      case "CARROT":
        return "당근";
      default:
        return channel;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "JOINED":
        return "참여중";
      case "IN_PROGRESS":
        return "진행중";
      case "COMPLETED":
        return "완료";
      case "FAILED":
        return "실패";
      case "CANCELED":
        return "취소됨";
      default:
        return status;
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold">
          미션 참여 취소 <br />
          (예치금 환불 & DB 수정 올인원 가능)
        </h1>
        <button
          onClick={() => navigate("/ops/mission/campaigns")}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
        >
          Ops Mission Admin으로 이동
        </button>
      </div>

      {/* 캠페인별 참여 목록 */}
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-4">캠페인별 참여 목록</h2>
        {campaignsLoading ? (
          <div>로딩 중...</div>
        ) : (
          <>
            {campaignsData?.missionAdminCampaigns && (
              <div className="mb-2 text-sm text-gray-600">
                총 {campaignsData.missionAdminCampaigns.length}개 중{" "}
                {Math.min(
                  campaignDisplayCount,
                  campaignsData.missionAdminCampaigns.length
                )}
                개 표시
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {campaignsData?.missionAdminCampaigns
                ?.slice(0, campaignDisplayCount)
                .map((campaign: any) => (
                  <div
                    key={campaign.id}
                    onClick={() => setSelectedCampaignId(campaign.id)}
                    className={clsx(
                      "border rounded-lg p-4 cursor-pointer transition-colors",
                      {
                        "border-blue-500 bg-blue-50":
                          selectedCampaignId === campaign.id,
                        "border-gray-200 hover:border-gray-300":
                          selectedCampaignId !== campaign.id,
                      }
                    )}
                  >
                    <div className="flex items-start gap-3">
                      {campaign.productImageUrl && (
                        <ResponsiveCDNImage
                          src={campaign.productImageUrl}
                          alt={campaign.productName}
                          className="w-16 h-16 rounded-md object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate">
                          {campaign.productName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {campaign.brand?.name || "브랜드 없음"}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {getChannelLabel(campaign.channel)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          총 참여:{" "}
                          {campaign.runs?.reduce(
                            (sum: number, run: any) =>
                              sum + (run.participationCount || 0),
                            0
                          ) || 0}
                          명
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
            {campaignsData?.missionAdminCampaigns &&
              campaignsData.missionAdminCampaigns.length >
                campaignDisplayCount && (
                <div className="text-center">
                  <button
                    onClick={() => setCampaignDisplayCount(campaignDisplayCount + 10)}
                    className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    더보기 (
                    {campaignsData.missionAdminCampaigns.length -
                      campaignDisplayCount}
                    개 더)
                  </button>
                </div>
              )}
          </>
        )}

        {/* 선택된 캠페인의 참여 목록 */}
        {selectedCampaignId && (
          <div className="mt-6">
            <h3 className="text-lg font-bold mb-4">참여 목록</h3>
            {participationsLoading ? (
              <div>로딩 중...</div>
            ) : allParticipations.length === 0 ? (
              <div className="text-gray-500">참여자가 없습니다.</div>
            ) : (
              <>
                <div className="mb-2 text-sm text-gray-600">
                  총 {allParticipations.length}명 중 {displayedParticipations.length}명 표시
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          참여 ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          사용자
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          전화번호
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          상태
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          예치금
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          참여일시
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          작업
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {displayedParticipations.map((participation: any) => (
                        <tr key={participation.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {participation.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {participation.user?.name || "없음"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {participation.user?.phone || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={clsx(
                                "px-2 inline-flex text-xs leading-5 font-semibold rounded-full",
                                {
                                  "bg-green-100 text-green-800":
                                    participation.status === "JOINED" ||
                                    participation.status === "IN_PROGRESS",
                                  "bg-blue-100 text-blue-800":
                                    participation.status === "COMPLETED",
                                  "bg-red-100 text-red-800":
                                    participation.status === "FAILED",
                                  "bg-gray-100 text-gray-800":
                                    participation.status === "CANCELED",
                                }
                              )}
                            >
                              {getStatusLabel(participation.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {participation.depositAmount.toLocaleString()}원
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {new Date(participation.joinedAt).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {participation.status !== "CANCELED" && (
                              <button
                                onClick={() =>
                                  setSelectedParticipationId(participation.id)
                                }
                                className="text-red-600 hover:text-red-800 font-medium"
                              >
                                취소
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {allParticipations.length > displayCount && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => {
                        console.log("더보기 클릭:", {
                          현재개수: displayCount,
                          전체개수: allParticipations.length,
                          추가후: displayCount + 10,
                        });
                        setDisplayCount(displayCount + 10);
                      }}
                      className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                    >
                      더보기 ({allParticipations.length - displayCount}개 더)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* 참여 ID로 직접 검색 */}
      <div className="border-t pt-8 mt-8">
        <h2 className="text-xl font-bold mb-4">참여 ID로 직접 검색</h2>
        <div className="flex items-center gap-2 mb-4">
          <input
            type="number"
            placeholder="참여 ID 입력"
            onKeyDown={handleKeyPress}
            className="border p-2 rounded w-1/2"
          />
          <button
            onClick={handleSearch}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            검색
          </button>
        </div>

        {loading && <div className="mb-4">로딩 중...</div>}
        {error && (
          <div className="mb-4 text-red-500">
            에러가 발생했습니다: {error.message}
          </div>
        )}

        {participation && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">참여 정보</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">참여 ID:</span>{" "}
                    <span className="font-medium">{participation.id}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">상태:</span>{" "}
                    <span
                      className={clsx("font-medium", {
                        "text-red-600": isCanceled,
                        "text-green-600": !isCanceled,
                      })}
                    >
                      {isCanceled ? "취소됨" : participation.status}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">참여일시:</span>{" "}
                    <span className="font-medium">
                      {new Date(participation.joinedAt).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700 mb-2">사용자 정보</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">이름:</span>{" "}
                    <span className="font-medium">
                      {participation.user.name || "없음"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">전화번호:</span>{" "}
                    <span className="font-medium">{participation.user.phone}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700 mb-2">미션 정보</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">브랜드:</span>{" "}
                    <span className="font-medium">
                      {participation.run.campaign.brand?.name || "없음"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">상품명:</span>{" "}
                    <span className="font-medium">
                      {participation.run.campaign.productName}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700 mb-2">금액 정보</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">예치금:</span>{" "}
                    <span className="font-medium">
                      {participation.depositAmount.toLocaleString()}원
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">상금:</span>{" "}
                    <span className="font-medium">
                      {participation.prizeAmount.toLocaleString()}원
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {!isCanceled && (
              <div className="mt-6">
                <div className="p-4 bg-gray-100 rounded-lg">
                  <h2 className="text-lg font-bold mb-2">취소 사유</h2>
                  <input
                    type="text"
                    value={cancelReason}
                    placeholder="취소 사유를 입력하세요 (4글자 이상)"
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="border p-2 rounded w-3/4"
                  />
                </div>
                <div
                  onClick={() => handleCancelParticipation(participation.id)}
                  className="mt-4 p-4 bg-red-500 rounded-lg text-center cursor-pointer hover:bg-red-600 transition-colors"
                >
                  <h2 className="text-lg font-bold text-white">참여 취소</h2>
                </div>
              </div>
            )}

            {isCanceled && (
              <div className="mt-4 p-4 bg-yellow-100 rounded-lg">
                <p className="text-yellow-800 font-medium">
                  이미 취소된 참여입니다.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 선택된 참여 취소 모달 */}
      {selectedParticipationId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4">참여 취소</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                취소 사유 (4글자 이상)
              </label>
              <input
                type="text"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="취소 사유를 입력하세요"
                className="border p-2 rounded w-full"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setSelectedParticipationId(null);
                  setCancelReason("");
                }}
                className="flex-1 border border-gray-300 rounded-lg py-2 font-semibold text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={() => handleCancelParticipation(selectedParticipationId)}
                disabled={cancelReason.length < 4}
                className="flex-1 bg-red-600 text-white rounded-lg py-2 font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCancelParticipation;
